import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import CircularProgress from "@mui/material/CircularProgress";
import {
  AlertTriangle,
  Send,
  RotateCcw,
  ChevronDown,
  Home,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSnackbar } from "../context/SnackbarContext";
import { sendSupportRequest } from "../services/supportService";

interface Props {
  error: Error;
  componentStack: string | null;
  onReset: () => void;
}

export function ErrorFallback({ error, componentStack, onReset }: Props) {
  const { user } = useAuth();
  const { showSuccess, showError } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reported, setReported] = useState(false);

  async function handleReport() {
    if (submitting || reported) return;
    setSubmitting(true);
    try {
      const subject = `Auto-report: ${error.message.slice(0, 160)}`;
      const message = formatBugReport({
        error,
        componentStack,
        pathname: location.pathname,
        search: location.search,
        user,
        note,
      });
      await sendSupportRequest({
        subject,
        message,
        url: window.location.href,
      });
      setReported(true);
      showSuccess("Bug report sent. Thanks for the help.");
    } catch (err) {
      const messageText =
        err instanceof Error ? err.message : "Couldn't send the report.";
      showError(messageText);
    } finally {
      setSubmitting(false);
    }
  }

  function handleGoHome() {
    onReset();
    navigate("/events");
  }

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <AlertTriangle size={28} color="#d32f2f" />
            <Typography variant="h5" component="h1">
              Something went wrong on this page
            </Typography>
          </Box>

          <Typography color="text.secondary">
            The page hit an unexpected error. The rest of the admin is still
            working, so you can try this page again, head back to your events,
            or report the bug to us with one click.
          </Typography>

          <Alert severity="error" variant="outlined">
            <Typography
              variant="body2"
              sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}
            >
              {error.message}
            </Typography>
          </Alert>

          {!reported && (
            <TextField
              label="Anything else we should know? (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What were you trying to do when this happened?"
              fullWidth
              multiline
              minRows={3}
              disabled={submitting}
            />
          )}

          <Accordion
            variant="outlined"
            sx={{ "&:before": { display: "none" } }}
          >
            <AccordionSummary expandIcon={<ChevronDown size={18} />}>
              <Typography variant="body2" color="text.secondary">
                Technical details
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  fontFamily: "monospace",
                  whiteSpace: "pre-wrap",
                  m: 0,
                  fontSize: "0.75rem",
                }}
              >
                {error.stack ?? error.message}
                {componentStack
                  ? `\n\nComponent stack:${componentStack}`
                  : ""}
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={
                submitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Send size={18} />
                )
              }
              onClick={handleReport}
              disabled={submitting || reported || !user}
            >
              {reported
                ? "Bug reported"
                : submitting
                  ? "Sending…"
                  : "Report this bug"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<RotateCcw size={18} />}
              onClick={onReset}
            >
              Try again
            </Button>
            <Button
              variant="text"
              startIcon={<Home size={18} />}
              onClick={handleGoHome}
            >
              Back to events
            </Button>
          </Stack>

          {!user && (
            <Alert severity="info" variant="outlined">
              You're signed out, so we can't auto-attach your account to the
              report. Sign in again to send it.
            </Alert>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}

interface FormatArgs {
  error: Error;
  componentStack: string | null;
  pathname: string;
  search: string;
  user: { uid: string; email: string | null } | null;
  note: string;
}

function formatBugReport(args: FormatArgs): string {
  const { error, componentStack, pathname, search, user, note } = args;
  const lines: string[] = [];
  lines.push(
    "**Auto-generated bug report from the admin dashboard error boundary.**",
  );
  lines.push("");
  if (note.trim()) {
    lines.push("## What the user said");
    lines.push(note.trim());
    lines.push("");
  }
  lines.push("## Error");
  lines.push("```");
  lines.push(error.message);
  lines.push("```");
  lines.push("");
  if (error.stack) {
    lines.push("## Stack");
    lines.push("```");
    lines.push(error.stack);
    lines.push("```");
    lines.push("");
  }
  if (componentStack) {
    lines.push("## Component stack");
    lines.push("```");
    lines.push(componentStack.trim());
    lines.push("```");
    lines.push("");
  }
  lines.push("## Context");
  lines.push(`- Path: \`${pathname}${search}\``);
  lines.push(`- URL: ${window.location.href}`);
  if (user) {
    lines.push(`- User UID: ${user.uid}`);
    if (user.email) lines.push(`- User email: ${user.email}`);
  } else {
    lines.push("- User: signed out at error time");
  }
  lines.push(`- Timestamp: ${new Date().toISOString()}`);
  lines.push(`- User agent: ${navigator.userAgent}`);
  return lines.join("\n");
}
